import { useCallback } from 'react';

import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import { useLazyFetchMessagesQuery, useMarkAsReadMutation, useSendMessageMutation } from '../api/app-chat.api';
import {
  addChat,
  addMessages,
  addOneMessage,
  setHasMoreMessages,
  setLastSendMessageTimeStamp,
} from '../store/app-chat.slice';
import { FetchMessagesPayload, SendMessagePayload } from '../types/app-chat.type';

interface UseChatMessages {
  sendMessageHandler: (payload: SendMessagePayload) => Promise<boolean>;
  fetchMessagesHandler: (payload: FetchMessagesPayload) => Promise<void>;
  markAsRead: () => Promise<void>;
}

export const useChatMessages = (): UseChatMessages => {
  const dispatch = useAppDispatch();

  const { activeChatId, chats } = useAppSelector((state) => state.appChat);

  const [sendMessageQuery] = useSendMessageMutation();
  const [fetchMessagesQuery] = useLazyFetchMessagesQuery();
  const [markAsReadQuery] = useMarkAsReadMutation();

  const sendMessageHandler = useCallback(
    async (payload: SendMessagePayload) => {
      const { data: response, error } = await sendMessageQuery(payload);
      const isError = error || !response?.data;
      if (isError) {
        toast.error(getApiErrorMessage(error) || 'Failed to send message!');
      } else {
        batch(() => {
          dispatch(addOneMessage(response.data));
          dispatch(setLastSendMessageTimeStamp(response.data.created_at));
        });
      }

      return !isError;
    },
    [chats],
  );

  const fetchMessagesHandler = async (payload: FetchMessagesPayload) => {
    const { data: response, error } = await fetchMessagesQuery(payload);
    const isError = error || !response?.data;
    if (isError) return;
    batch(() => {
      dispatch(addMessages({ chatId: payload.chat_id, messages: response.data, append: true }));
      dispatch(setHasMoreMessages({ chatId: payload.chat_id, hasMore: response.meta.has_more }));
    });
  };

  const markAsRead = useCallback(async () => {
    if (activeChatId) {
      const chat = chats[activeChatId];

      if (chat) {
        const lastChatMessage = chat?.last_message;
        if (lastChatMessage) {
          const { data: response, error } = await markAsReadQuery({
            chat_id: chat.id,
            last_read_message_id: lastChatMessage.id,
          });
          const isError = error || !response?.data;
          if (isError) return;

          dispatch(addChat(response.data));
        }
      }
    }
  }, [activeChatId, chats]);

  return { sendMessageHandler, fetchMessagesHandler, markAsRead };
};
